import {
  LayerCollection,
  MapProperties,
  BufferToolSwitcher,
} from '@datum-group/datum-map/lib';
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { SelectionRecord } from '@datum-group/datum-map/lib/Tools/BufferZone/types';
import { MapUnitConvertor } from '@datum-group/datum-map/lib/common/';
import { SelectedLayer } from './SelectedLayer';

export class BufferZone {
  readonly switcher: BufferToolSwitcher;
  private _mapProperties: MapProperties;
  private _layerCollection: LayerCollection;
  private _convertor: MapUnitConvertor;
  @observable private _layers: Record<string, SelectedLayer> = {};
  @observable private _activeLayer?: SelectedLayer = undefined;

  constructor(
    mapProperties: MapProperties,
    layerCollection: LayerCollection,
    convertor: MapUnitConvertor,
  ) {
    this._mapProperties = mapProperties;
    this._layerCollection = layerCollection;
    this._convertor = convertor;

    this.switcher = new BufferToolSwitcher(
      this._mapProperties.map,
      this._layerCollection,
      this._convertor.meterToMapUnit(500),
    );

    this._bind();
    reaction(() => this.switcher.tool, this._bind);

    makeObservable(this);
  }

  @computed get layers() {
    return Object.values(this._layers);
  }

  @computed get activeLayer() {
    return this._activeLayer;
  }

  @action setActiveLayer = (name: string) => {
    const layer: SelectedLayer | undefined = this._layers[name];

    if (layer) runInAction(() => (this._activeLayer = layer));
  };

  @action clearActiveLayer = () => {
    runInAction(() => (this._activeLayer = undefined));
  };

  @action clear = () => {
    this.switcher.tool.clearError();
    runInAction(() => {
      this._activeLayer = undefined;
      this._layers = {};
    });
  };

  private _bind = () => {
    this.clear();

    this.switcher.tool.subscribe(this._handleSelection);
  };

  private _handleSelection = (selection: SelectionRecord[]) => {
    this.clear();
    this._process(selection);
  };

  private _process(selection: SelectionRecord[]) {
    selection.forEach((elem) => {
      const layer = this._getLayer(elem);
      layer.addSelection(elem);
    });
  }

  private _getLayer(selection: SelectionRecord): SelectedLayer {
    const { layerName, layerDescription, tableName } = selection;

    let layer: SelectedLayer | undefined = this._layers[layerName];

    if (layer) return layer;

    layer = new SelectedLayer(layerName, tableName, layerDescription);
    this._layers[layerName] = layer;

    return layer;
  }
}
